'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faDownload, faCaretDown } from '@fortawesome/pro-solid-svg-icons';

interface AnalyticsExportButtonProps {
  onExport?: (format: 'csv' | 'xlsx') => void;
  fullWidth?: boolean;
  disabled?: boolean;
}

const AnalyticsExportButton: React.FC<AnalyticsExportButtonProps> = ({
  onExport,
  fullWidth = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    if (disabled) return;

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsOpen(!isOpen);
  }, [isOpen, disabled]);

  const handleExport = useCallback(
    (format: 'csv' | 'xlsx') => {
      if (disabled) return;
      
      if (onExport) {
        onExport(format);
      }
      setIsOpen(false);
    },
    [onExport, disabled]
  );

  return (
    <div
      data-testid="export-button-container"
      aria-label="Export analytics data"
      className={fullWidth ? 'w-full' : ''}
    >
      <div
        ref={buttonRef}
        className={`px-4 h-9 bg-white rounded-full border border-neutral-200 inline-flex justify-between gap-2 items-center ${
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:outline-neutral-400'
        } ${fullWidth ? 'w-full' : ''}`}
        onClick={toggleDropdown}
        data-testid="analytics-export-button"
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-disabled={disabled}
      >
        <div className="flex items-center gap-1.5 justify-center flex-1">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <Icon
              icon={faDownload}
              className="w-4 h-4 text-secondary"
              data-testid="analytics-export-icon"
            />
          </div>
          <span
            data-testid="analytics-export-label"
            className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1"
          >
            Export
          </span>
        </div>
        <div className="flex items-center flex-shrink-0">
          <Icon
            icon={faCaretDown}
            className={`text-secondary w-4 h-4 transition-all duration-300 ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen &&
        dropdownPosition &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            aria-label="Export format options"
            className="fixed bg-white border border-neutral-200 rounded-lg shadow-lg py-2 min-w-[180px] z-50"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
            data-testid="analytics-export-dropdown"
          >
            <button
              role="menuitem"
              className="w-full text-left px-4 py-2 text-sm font-medium text-primary hover:bg-neutral-100 focus:outline-none focus:bg-neutral-100"
              onClick={() => handleExport('csv')}
              data-testid="analytics-export-csv"
            >
              Export as CSV
            </button>
            <button
              role="menuitem"
              className="w-full text-left px-4 py-2 text-sm font-medium text-primary hover:bg-neutral-100 focus:outline-none focus:bg-neutral-100"
              onClick={() => handleExport('xlsx')}
              data-testid="analytics-export-xlsx"
            >
              Export as Excel
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AnalyticsExportButton;
